import { defineStore } from 'pinia';
import { computed, ref, markRaw } from 'vue-demi';
import { Data, DataTypePlural, Filter } from '@ilteducation/content-ref-data-types';
import { getRefDataByIds, getRefDatas, Response } from './api';

type ContentRefDataState = {
  refDataType: DataTypePlural;
  isLoading: boolean;
  contents: Data[];
};

type RefDataByType = Record<DataTypePlural, ContentRefDataState | undefined>;

const emptyState: RefDataByType = {
  categories: undefined,
  languages: undefined,
  publishers: undefined,
  series: undefined,
  areas: undefined,
  keywords: undefined,
  contributors: undefined,
};

const useContentRefData = defineStore('content-ref-data', () => {
  const state = ref<RefDataByType>({ ...emptyState });

  const isLoading = computed(() => (type: DataTypePlural) => state.value[type]?.isLoading ?? false);
  const all = computed(
    () =>
      (type: DataTypePlural): Data[] =>
        state.value[type]?.contents ?? [],
  );
  const byId = computed(
    () =>
      (type: DataTypePlural, referenceId: string): Data | undefined =>
        state.value[type]?.contents.find((data) => data.id === referenceId),
  );

  const loadReferences = async (refDataType: DataTypePlural, filters?: Filter[], query?: string) => {
    if (!state.value[refDataType]) {
      state.value[refDataType] = { refDataType, isLoading: false, contents: [] };
    }

    const typeState = state.value[refDataType]!;

    if (typeState.isLoading) {
      return;
    }

    typeState.isLoading = true;
    try {
      let cursor: string | null = null;
      const allNewData: Data[] = [];

      do {
        const res: Response<Data> = await getRefDatas<Data>(refDataType, cursor, filters, query);
        allNewData.push(...(res.data as Data[]).map((item) => markRaw(item as Data)));
        cursor = res.cursor;
      } while (cursor);

      const combinedData = [...typeState.contents, ...allNewData];
      typeState.contents = Array.from(new Map(combinedData.map((item) => [item.id, item])).values());
    } catch (error) {
      console.error(`Failed to load reference data for ${refDataType}:`, error);
    } finally {
      typeState.isLoading = false;
    }
  };

  const loadByIds = async (refDataType: DataTypePlural, ids: string[]): Promise<Data[]> => {
    const notLoadedIds = ids.filter((id) => !byId.value(refDataType, id));
    const alreadyLoaded = ids.map((id) => byId.value(refDataType, id)).filter((data): data is Data => !!data);

    if (notLoadedIds.length === 0) {
      return alreadyLoaded;
    }

    if (!state.value[refDataType]) {
      state.value[refDataType] = { refDataType, isLoading: false, contents: [] };
    }
    const typeState = state.value[refDataType]!;
    typeState.isLoading = true;

    try {
      const newRefData = await getRefDataByIds(notLoadedIds);
      typeState.contents.push(...newRefData.map((item) => markRaw(item)));
      return [...alreadyLoaded, ...newRefData];
    } catch (error) {
      console.error(`Failed to load reference data by IDs for ${refDataType}:`, error);
      return alreadyLoaded;
    } finally {
      typeState.isLoading = false;
    }
  };

  const $reset = () => {
    state.value = { ...emptyState };
  };

  const clear = () => {
    $reset();
  };

  return {
    all,
    byId,
    loadByIds,
    isLoading,
    references: loadReferences,
    clear,
    result: all, // deprecated
    reference: byId, // deprecated
  };
});

export default useContentRefData;
